import { lazy, Suspense } from 'react';
import { MemoryRouter } from 'react-router-dom';
import { useFloatingChat } from '@/core/contexts/FloatingChatContext';
import { ROUTES } from '@/core/routes/constants';

// ═══════════════════════════════════════════════════════════════════════════════
// CRITICAL PERFORMANCE: Lazy load ChatWidget
// ChatWidget + dependencies add ~150-200KB to the main bundle if imported sync.
// By lazy loading, we only load the chat code when the user actually clicks.
// ═══════════════════════════════════════════════════════════════════════════════
const ChatWidget = lazy(() => 
  import('@/features/chat/components/ChatWidget').then(m => ({ default: m.ChatWidget }))
);

// Loading fallback for the chat widget
function ChatLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full bg-white dark:bg-secondary-900">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-secondary-500 dark:text-secondary-400">Cargando chat...</span>
      </div>
    </div>
  );
}

interface FloatingBubblesProps {
  /** WhatsApp phone number (optional - will be added later) */
  whatsappNumber?: string;
  /** Default WhatsApp message */
  whatsappMessage?: string;
  /** Bot name for the chat widget */
  botName?: string;
  /** Bot ID for the chat widget */
  botId?: string;
  /** Primary color for the chat bubble */
  chatPrimaryColor?: string;
  /** Routes where bubbles should be hidden */
  hiddenRoutes?: string[];
}

/**
 * FloatingBubbles - Classic floating action buttons
 * 
 * Includes:
 * - WhatsApp button (opens WhatsApp chat)
 * - Chat button (opens the sales bot popup)
 * 
 * Uses FloatingChatContext to persist state across navigation
 * Hidden on /chat route (extended chat view) and /admin routes
 */
export function FloatingBubbles({
  whatsappNumber = '',
  whatsappMessage = '¡Hola! Me gustaría obtener más información.',
  botName = 'Ventas',
  botId,
  chatPrimaryColor = '#4f46e5',
  hiddenRoutes = [ROUTES.CHAT, '/admin'],
}: FloatingBubblesProps) {
  const { isOpen, openChat, closeChat, pathname } = useFloatingChat();

  // Check if current route should hide the bubbles
  const shouldHide = hiddenRoutes.some((route) => pathname.startsWith(route));

  // Don't render anything if on a hidden route
  if (shouldHide) {
    return null;
  }

  // Generate WhatsApp URL
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=${encodeURIComponent(whatsappMessage)}`
    : '#';

  const handleWhatsAppClick = () => {
    if (!whatsappNumber) {
      console.warn('[FloatingBubbles] WhatsApp number not configured');
      return;
    }
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {/* Floating buttons container - hidden when chat is open */}
      <div className={`floating-bubbles-container fixed bottom-24 right-4 md:bottom-6 md:right-6 z-40 flex flex-col gap-3 items-end transition-all duration-300 ${isOpen ? 'hidden' : ''}`}>
        {/* WhatsApp Button */}
        <button
          onClick={handleWhatsAppClick}
          className="group w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
          aria-label="Contactar por WhatsApp"
          title="Contactar por WhatsApp"
        >
          {/* WhatsApp Icon */}
          <svg
            className="w-7 h-7 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>

          {/* Tooltip */}
          <span className="absolute right-16 bg-secondary-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none shadow-lg">
            WhatsApp
          </span>
        </button>

        {/* Chat/Bot Button */}
        <button
          onClick={openChat}
          className="group w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2"
          style={{
            backgroundColor: chatPrimaryColor,
            '--tw-ring-color': chatPrimaryColor,
          } as React.CSSProperties}
          aria-label="Abrir chat de ventas"
          title="Abrir chat de ventas"
        >
          {/* Chat Icon */}
          <svg
            className="w-7 h-7 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>

          {/* Tooltip */}
          <span className="absolute right-16 bg-secondary-900 text-white text-sm px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none shadow-lg">
            Chat de ventas
          </span>
        </button>
      </div>

      {/* Chat Widget Popup - Lazy loaded, wrapped in MemoryRouter for Link/navigate support */}
      {isOpen && (
        <div className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 w-96 max-w-[calc(100vw-3rem)] h-[500px] max-h-[calc(100vh-3rem)] rounded-2xl shadow-2xl overflow-hidden">
          <MemoryRouter>
            <Suspense fallback={<ChatLoadingFallback />}>
              <ChatWidget
                config={{
                  botName: botId ? undefined : botName,
                  botId: botId,
                  primaryColor: chatPrimaryColor,
                  position: 'bottom-right',
                  expanded: true,
                }}
                embedded={true}
                onClose={closeChat}
              />
            </Suspense>
          </MemoryRouter>
        </div>
      )}
    </>
  );
}
